import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { ApiService } from 'src/app/shared/services/api.service';
import { LrsService, LrsRoute } from 'src/app/shared/services/lrs.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

@Component({
  selector: 'app-add-survey',
  templateUrl: './add-survey.component.html',
  styleUrl: './add-survey.component.css',
})
export class AddSurveyComponent implements OnInit {
  surveyForm!: FormGroup;
  selectedDomain = 'Residential';
  domainOptions: string[] = [
    'Residential', 'Commercial', 'Educational', 'Water', 'Power', 'Gas', 'Telecom', 'Road', 'Consultancy',
  ];
  optionsMap: { [key: string]: { value: string; label: string }[] } = {
    domainOptionsDrp: this.domainOptions.map((d) => ({ value: d, label: d })),
  };
  recordId: number | null = null;
  saving = false;
  lrsRoutes: LrsRoute[] = [];
  queryProjectId: string | null = null;
  queryProjectName = '';

  domainFields: { [key: string]: { key: string; label: string; type: string; required?: boolean; value?: unknown }[] } = {
    Residential: [
      { key: 'plotBoundaries', label: 'Plot Boundaries', type: 'text', value: '' },
      { key: 'soilComposition', label: 'Soil Composition', type: 'text', value: '' },
      { key: 'elevationProfile', label: 'Elevation Profile', type: 'text', value: '' },
    ],
    Commercial: [
      { key: 'utilityAccessPoints', label: 'Utility Access Points', type: 'text', value: '' },
      { key: 'trafficDensitySurvey', label: 'Traffic Density Survey', type: 'text', value: '' },
      { key: 'noiseLevelAssessment', label: 'Noise Level Assessment', type: 'text', value: '' },
    ],
    Educational: [
      { key: 'accessibilityMapping', label: 'Accessibility Mapping', type: 'text', value: '' },
      { key: 'environmentalImpact', label: 'Environmental Impact', type: 'text', value: '' },
      { key: 'landGradient', label: 'Land Gradient', type: 'number', value: '' },
    ],
    Water: [
      { key: 'waterTableDepth', label: 'Water Table Depth', type: 'text', value: '' },
      { key: 'sourceIdentification', label: 'Source Identification', type: 'text', value: '' },
      { key: 'flowRateMeasurement', label: 'Flow Rate Measurement', type: 'text', value: '' },
    ],
    Power: [
      { key: 'gridProximity', label: 'Grid Proximity', type: 'text', value: '' },
      { key: 'terrainAnalysis', label: 'Terrain Analysis', type: 'text', value: '' },
      { key: 'emfSurvey', label: 'EMF Survey', type: 'text', value: '' },
    ],
    Gas: [
      { key: 'pipelineCorridorWidth', label: 'Pipeline Corridor Width', type: 'text', value: '' },
      { key: 'soilStabilityIndex', label: 'Soil Stability Index', type: 'text', value: '' },
      { key: 'leakRiskZones', label: 'Leak Risk Zones', type: 'text', value: '' },
    ],
    Telecom: [
      { key: 'signalInterferenceZones', label: 'Signal Interference Zones', type: 'text', value: '' },
      { key: 'towerBaseCoordinates', label: 'Tower Base Coordinates', type: 'text', value: '' },
      { key: 'fiberRouteFeasibility', label: 'Fiber Route Feasibility', type: 'text', value: '' },
    ],
    Road: [
      { key: 'pavementConditionIndex', label: 'Pavement Condition Index', type: 'number', value: '' },
      { key: 'rightOfWayWidth', label: 'Right-of-Way Width', type: 'text', value: '' },
      { key: 'trafficSurveyPoints', label: 'Traffic Survey Points', type: 'number', value: '' },
    ],
    Consultancy: [
      { key: 'surveyScopeDefinition', label: 'Survey Scope Definition', type: 'text', value: '' },
      { key: 'clientSpecificMetrics', label: 'Client-Specific Metrics', type: 'text', value: '' },
      { key: 'surveyReviewTimeline', label: 'Survey Review Timeline', type: 'date', value: '' },
    ],
  };

  commonFields = [
    { key: 'projectName', label: 'Project Name', type: 'text', required: true, value: '' },
    { key: 'projectId', label: 'Project ID', type: 'hidden', required: false, value: '' },
    { key: 'surveyLead', label: 'Survey Lead', type: 'text', required: true, value: '' },
    { key: 'surveyStartDate', label: 'Survey Start Date', type: 'date', required: true, value: '' },
    { key: 'surveyEndDate', label: 'Survey End Date', type: 'date', required: true, value: '' },
    { key: 'surveyType', label: 'Survey Type', type: 'text', required: true, value: '' },
    { key: 'equipmentUsed', label: 'Equipment Used', type: 'text', required: true, value: '' },
    { key: 'dataCollectionMethod', label: 'Data Collection Method', type: 'text', required: true, value: '' },
    { key: 'accuracyLevel', label: 'Accuracy Level', type: 'hidden', required: false, value: '' },
    { key: 'surveyReportReference', label: 'Survey Report Reference', type: 'text', required: true, value: '' },
    {
      key: 'domain',
      label: 'Domain',
      type: 'select',
      optionKey: 'domainOptionsDrp',
      required: true,
      value: 'Residential',
      onChange: (value: string) => this.onDomainChange(value),
    },
  ];

  pickerMap: { [key: string]: MatDatepicker<unknown> } = {};

  constructor(
    private fb: FormBuilder,
    private readonly apiService: ApiService,
    private readonly lrsService: LrsService,
    private readonly toastrMsgService: ToastrMsgService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.surveyForm = this.fb.group({});
    this.commonFields.forEach((field) => {
      const validators = field.required ? [Validators.required] : [];
      this.surveyForm.addControl(field.key, this.fb.control(field.value ?? '', validators));
    });
    this.onDomainChange(this.selectedDomain);
    this.surveyForm.addControl('lrsRouteId', this.fb.control<number | null>(null));
    this.surveyForm.addControl('lrsFromKm', this.fb.control<number | null>(null));
    this.surveyForm.addControl('lrsToKm', this.fb.control<number | null>(null));
    this.surveyForm.addControl('lrsChainage', this.fb.control(''));

    this.route.queryParams.subscribe((params) => {
      const projectId = params['projectId'];
      const projectName = params['projectName'];
      this.queryProjectId = projectId || null;
      this.queryProjectName = projectName || '';
      if (projectId && this.surveyForm) {
        this.surveyForm.patchValue({
          projectId: Number(projectId) || projectId,
          projectName: projectName || '',
        });
        this.loadLrsRoutes(String(projectId));
      } else {
        this.loadLrsRoutes();
      }
    });

    this.route.params.subscribe((params) => {
      const id = params['id'];
      if (id) {
        this.recordId = Number(id);
        this.loadRecord(this.recordId);
      }
    });
  }

  private loadRecord(id: number): void {
    this.apiService.getProjectSurveyStageById(id).subscribe({
      next: (res: { data?: Record<string, unknown> }) => {
        const row = res.data || (res as Record<string, unknown>);
        const remarks = this.parseRemarks(row['remarks']);
        this.surveyForm.patchValue({
          projectName: row['project_name'],
          projectId: row['project_id'],
          surveyLead: row['survey_lead'],
          surveyStartDate: row['survey_start_date'],
          surveyEndDate: row['survey_end_date'],
          surveyType: remarks['surveyType'],
          equipmentUsed: remarks['equipmentUsed'],
          dataCollectionMethod: remarks['dataCollectionMethod'],
          accuracyLevel: remarks['accuracyLevel'],
          surveyReportReference: remarks['surveyReportReference'],
          domain: row['domain'] || 'Residential',
        });
        this.onDomainChange(String(row['domain'] || 'Residential'));
        if (remarks['domainFields']) {
          this.surveyForm.patchValue(remarks['domainFields'] as Record<string, unknown>);
        }
        const lrs = (remarks['lrs'] as Record<string, unknown>) || {};
        this.surveyForm.patchValue({
          lrsRouteId: lrs['route_id'] != null ? Number(lrs['route_id']) : null,
          lrsFromKm: lrs['from_km'] != null ? Number(lrs['from_km']) : null,
          lrsToKm: lrs['to_km'] != null ? Number(lrs['to_km']) : null,
          lrsChainage: lrs['chainage'] || '',
        });
        this.loadLrsRoutes(row['project_id'] != null ? String(row['project_id']) : undefined);
      },
      error: (err) => this.toastrMsgService.error(err?.error?.message || 'Failed to load survey'),
    });
  }

  private parseRemarks(raw: unknown): Record<string, unknown> {
    if (!raw) return {};
    if (typeof raw === 'object') return raw as Record<string, unknown>;
    try {
      return JSON.parse(String(raw));
    } catch {
      return {};
    }
  }

  private buildPayload(): Record<string, unknown> {
    const v = this.surveyForm.value;
    const domainKeys = (this.domainFields[this.selectedDomain] || []).map((f) => f.key);
    const domainFields: Record<string, unknown> = {};
    domainKeys.forEach((k) => {
      if (v[k] !== undefined && v[k] !== '') domainFields[k] = v[k];
    });
    return {
      project_id: v.projectId || this.queryProjectId,
      project_name: v.projectName || this.queryProjectName,
      survey_lead: v.surveyLead,
      survey_start_date: v.surveyStartDate,
      survey_end_date: v.surveyEndDate,
      domain: v.domain,
      stage: 'Survey',
      status: 'in_progress',
      remarks: JSON.stringify({
        surveyType: v.surveyType,
        equipmentUsed: v.equipmentUsed,
        dataCollectionMethod: v.dataCollectionMethod,
        accuracyLevel: v.accuracyLevel,
        surveyReportReference: v.surveyReportReference,
        domainFields,
        lrs: this.isRoadDomain && v.lrsRouteId
          ? {
              route_id: v.lrsRouteId,
              from_km: v.lrsFromKm,
              to_km: v.lrsToKm,
              chainage: v.lrsChainage || undefined,
            }
          : undefined,
      }),
    };
  }

  loadLrsRoutes(projectId?: string): void {
    this.lrsService.listRoutes(projectId ? { project_id: projectId } : undefined).subscribe({
      next: (res) => {
        this.lrsRoutes = res.data || [];
      },
      error: () => {
        this.lrsRoutes = [];
      },
    });
  }

  openSurveyOnMap(): void {
    const v = this.surveyForm.value;
    const queryParams: Record<string, string | number> = { tab: 'lrs' };
    if (v.lrsRouteId) queryParams['route_id'] = v.lrsRouteId;
    if (v.lrsFromKm != null) queryParams['from_km'] = v.lrsFromKm;
    if (v.lrsToKm != null) queryParams['to_km'] = v.lrsToKm;
    if (v.projectId) queryParams['project_id'] = v.projectId;
    void this.router.navigate(['/home/geospatial'], { queryParams });
  }

  onSubmit(): void {
    if (this.queryProjectId && !this.surveyForm.value.projectId) {
      this.surveyForm.patchValue({
        projectId: this.queryProjectId,
        projectName: this.surveyForm.value.projectName || this.queryProjectName,
      });
    }
    if (this.surveyForm.invalid || !(this.surveyForm.value.projectId || this.queryProjectId)) {
      this.surveyForm.markAllAsTouched();
      this.toastrMsgService.error('Please fill all required fields');
      return;
    }
    this.saving = true;
    const payload = this.buildPayload();
    const req = this.recordId
      ? this.apiService.updateProjectSurveyStage(this.recordId, payload)
      : this.apiService.saveProjectSurveyStage(payload);

    req.subscribe({
      next: () => {
        this.saving = false;
        this.toastrMsgService.success(this.recordId ? 'Survey updated' : 'Survey saved');
        this.navigateAfterSave();
      },
      error: (err) => {
        this.saving = false;
        this.toastrMsgService.error(err?.error?.message || err?.error?.errorMessage || 'Save failed');
      },
    });
  }

  onReset(): void {
    this.surveyForm.reset();
    this.onDomainChange(this.selectedDomain);
    if (this.queryProjectId) {
      this.surveyForm.patchValue({
        projectId: this.queryProjectId,
        projectName: this.queryProjectName,
      });
    }
  }

  goBack(): void {
    if (this.queryProjectId) {
      this.router.navigate(['/home/survey-list'], {
        queryParams: this.surveyListQueryParams(this.queryProjectId, this.queryProjectName),
      });
      return;
    }
    this.router.navigate(['/home/projects'], { queryParams: { stage: 'Survey' } });
  }

  private navigateAfterSave(): void {
    const projectId = this.queryProjectId || this.surveyForm.value.projectId;
    const projectName = this.queryProjectName || this.surveyForm.value.projectName;
    if (projectId) {
      this.router.navigate(['/home/survey-list'], {
        queryParams: this.surveyListQueryParams(projectId, projectName),
      });
      return;
    }
    this.router.navigate(['/home/survey-list']);
  }

  private surveyListQueryParams(projectId: string | number, projectName?: string): Record<string, string> {
    const params: Record<string, string> = { projectId: String(projectId) };
    if (projectName) params['projectName'] = String(projectName);
    return params;
  }

  get isRoadDomain(): boolean {
    const domain = this.surveyForm?.get('domain')?.value ?? this.selectedDomain;
    return String(domain).toLowerCase() === 'road';
  }

  onDomainChange(domain: string): void {
    this.selectedDomain = domain;
    if (!this.isRoadDomain && this.surveyForm.contains('lrsRouteId')) {
      this.surveyForm.patchValue({
        lrsRouteId: null,
        lrsFromKm: null,
        lrsToKm: null,
        lrsChainage: '',
      });
    }
    Object.keys(this.surveyForm.controls).forEach((key) => {
      if (
        key !== 'domain' &&
        this.domainFields[domain]?.find((field) => field.key === key) === undefined &&
        Object.values(this.domainFields).flat().some((field) => field.key === key)
      ) {
        this.surveyForm.removeControl(key);
      }
    });
    (this.domainFields[domain] || []).forEach((field) => {
      if (!this.surveyForm.contains(field.key)) {
        this.surveyForm.addControl(field.key, this.fb.control(field.value ?? ''));
      }
    });
  }
}
